import { api } from './http'
import type { OnboardingProgress, PortalMode, CustomerType } from '@/constants/dicts'

/**
 * 登录响应（接口 2.0 已重写这个 schema）。
 *
 * 1.0 时它直接复用了 CRM 的 `Profile`（带 roleCode / dataScope / permissions / menus），
 * 而客户端每页都要的 portalMode / onboardingProgress / unifiedAccount 反而不在里面。
 * **2.0 修好了**：后台字段全部移除，三个客户端字段都进来了，登录后不必再多调一次
 * `/profile/me` 才能判门禁。
 *
 * ✅ **接口 4.0 把 `passwordExpireInDays` 补回来了**，
 * 而且三处都有：`LoginResult` / `AuthedResult` / `ProfileResult` ——
 * 刷新页面走 `/profile/me` 也拿得到，不必依赖登录那一次。
 * 需求 §5.3 的 14 / 7 / 3 分级提醒因此**全部恢复**，前端代码一行没改（本来就写好了）。
 *
 * ⭐ **接口 0.10.0（2026-09-08，用户中心 V1.4 §5.4）改了登录的成败语义**：
 * **准入被拒也是 HTTP 200**，`token=null` + `denyReason`，而不是抛 40313。
 * 因为「审核中 / 申请未通过 / 账户已停用」在客户那边是三件完全不同的事，
 * 一个错误码回不出区别；两份需求文档也都把它定义为响应字段。
 * 40313 自此只剩「会话中途被停用」一个出口（前端只需立刻登出，不必分流）。
 *
 * 同批新增 `clientType`（前端整套分流的依据）、`mt5Status`（优先于 accountOpening）、
 * `mustChangePassword`（首登强制改密——公司户授权代表的初始密码是明文随开通信发出去的）。
 */
/** 首户开立状态（接口 0.8.4）：PROVISIONING=建户后台重做中；FAILED=CRM 确认不开、提案已取消 */
export type AccountOpening = 'PROVISIONING' | 'FAILED'

/**
 * 登录准入被拒的原因（0.10.0）。三种在客户那边是三件不同的事：
 *   under_review          —— 开户申请正在审核中（查重命中待客服核实）。**0.13.5（决策 023）起不再返回**：
 *                            查重核实中可以登录、进 DUP 受限模式；枚举保留兼容旧后端；
 *   application_declined  —— 开户申请未能通过。**0.13.5 起不再返回**：申请未通过的停用与其他停用一样回 portal_disabled，不披露原因；
 *   portal_disabled       —— 统一账号登录已停用（后台手工 / 名下真实账户全部禁用或关闭 / 开户申请未通过）。
 */
export type LoginDenyReason = 'under_review' | 'application_declined' | 'portal_disabled'

/**
 * 客户类型（0.10.0）。**整套前端分流挂在这一个字段上**（菜单、任务弹窗、公司页），
 * 取不到时按 R-23 拒绝进门户——宁可让客户重登一次，也不要渲染出一个类型不明的门户。
 */
export type ClientType = 'retail' | 'wholesale'

/**
 * MT5 开通状态（0.10.0，用户中心 V1.4 §5.5）。**前端优先于 accountOpening**：
 *   opening —— 有在途首开提案，或建户掉单但仍在补单窗口内（缺省 24h）；
 *   opened  —— 已开通；
 *   failed  —— 掉单超窗或首开提案已取消。前台文案是「开通遇到问题，我们的团队正在处理」，不说失败。
 */
export type Mt5Status = 'opening' | 'opened' | 'failed'

export interface LoginResult {
  /** **被拒时为 null**（此时看 denyReason），不是「登录成功但 token 丢了」 */
  token: string | null
  // 不含统一账号：它仅后台显示，后端 2026-09-15 起不再下发
  /** 法定姓名 */
  fullName: string
  email: string
  onboardingProgress: OnboardingProgress
  portalMode: PortalMode
  /** 客户类型（接口 0.8.0；二期 P01 起四值 RETAIL / SOPH_IND / WHOLESALE / SOPH_CORP）：只用于展示与零售保护，分流看 clientType */
  customerType: CustomerType
  /**
   * 客户类型的小写形态（0.10.0）。与 customerType 同源，但**前端一律用它**：
   * 需求与原型里的分流条件写的都是 retail / wholesale。缺失＝后端版本过旧，按 R-23 拒绝进门户。
   */
  clientType?: ClientType | null
  /** 语言偏好 zh-CN / zh-HK / en-AU */
  locale: string
  /**
   * 首户开立状态（接口 0.8.4）。只在进度为 PENDING_KYC / PENDING_ACCOUNT 时有值：
   *   PROVISIONING —— KYC 已通过但 MT5 建户未确认（首开提案 REDO，CRM 人工重做中）：两项任务都已完成、只是账户还没开出来，
   *                    首页 / 任务弹窗 / 认证页显示「账户配置中」，不再引导回认证页；
   *   FAILED —— 首开提案已被 CRM 取消（人工确认不开）：显示「未能开立，请联系客服」，不披露原因。
   * 旧后端没有这个字段 → 当 null（原行为）。
   */
  accountOpening?: AccountOpening | null

  /**
   * 登录密码剩余有效天数（90 天周期，接口 4.0 补回）：
   * 15–90 静默；≤14 资料页显示；≤7 首页黄条可关；≤3 红条不可关 + 登录后弹一次；
   * **≤0 已过期** —— 此时 token 带过期标记，除改密 / 登出 / profile/me 外一律 40301。
   */
  passwordExpireInDays?: number | null
  /**
   * 首登强制改密（0.10.0 起后端真的会给）：公司户授权代表的初始密码是明文随开通信发出去的，
   * 首登必须先改；凭据被轮换后同理。为 true 时除改密 / 登出 / profile/me 外一律 40301，
   * 与「密码已过期」同一道闸、同一个页面，只是文案不同。
   */
  mustChangePassword?: boolean
  /** MT5 开通状态（0.10.0）。前端优先于 accountOpening */
  mt5Status?: Mt5Status | null
  /** **非 null＝准入被拒**（此时 token 为 null，其余字段一律不下发） */
  denyReason?: LoginDenyReason | null
}

/**
 * 改密响应（接口 4.0 新增 `ChangePasswordResult`）。
 *
 * ⭐ **4.0 起改密不再踢人重登**：本会话保留，后端换发一个新 token
 * （旧 token 立即失效，sid 已轮换），其他设备全部退出。
 * 前端换用新 token 即可继续，横幅与弹窗按新的 90 天即时消失、无需刷新。
 *
 * ✅ 接口 5.0 已把 200 响应 `$ref` 到本 schema。
 * 4.0 时它定义了却没人引用，按 spec 生成的客户端会以为改密返回空。
 * 这里保留对 data 为空的兼容（退回「跳登录页重登」）—— 联调环境若还跑着旧版，不会把客户卡死。
 */
export interface ChangePasswordResult {
  token: string
  /** 重置后的剩余天数（=90） */
  passwordExpireInDays: number
}

export interface LoginRequest {
  /** 邮箱，或 MT5 账号（6~10 位纯数字自动判定） */
  account: string
  password: string
}

export const authApi = {
  login: (body: LoginRequest) => api.post<LoginResult>('/c/auth/login', body),
  logout: () => api.post<null>('/c/auth/logout'),

  /** 统一提示「若该邮箱已注册，重置链接已发送」，不暴露邮箱是否存在（§5.2） */
  forgotPassword: (email: string) => api.post<null>('/c/auth/forgot-password', { email }),

  /** 凭邮件链接重置。链接失效返回 42811 */
  resetPassword: (body: { token: string; newPassword: string }) =>
    api.post<null>('/c/auth/reset-password', body),

  /**
   * 修改登录密码（同一张表单也用于 R-18 的过期强制页）。
   *
   * 规则 4.0 起：8–20 位、含大写 + 小写 + 数字、不得与当前密码相同、
   * **且不得与最近 5 次相同**（最后一条 2.0 时被删掉、4.0 恢复；前端无法本地校验，
   * 由后端 422 返回，message 可直接展示）。
   *
   * `oldPassword` **只在首登强制改密（`mustChangePassword`）时可省**：那个会话是几秒前
   * 用初始密码换来的，需求 §5.3.3 的表单也只有新密码两栏。后端按库里的 `must_change`
   * 放行，并照样比对当前 hash 保证「不得与初始密码相同」。其余入口必传。
   */
  changePassword: (body: { oldPassword?: string; newPassword: string }) =>
    api.post<ChangePasswordResult | null>('/c/auth/password', body),

  /**
   * 凭邮件链接重置 **MT5 交易密码**（接口 7.0 新增，公开端点；，产品选了「做」）。
   *
   * 链接由 `accountApi.requestMt5PasswordReset` 发出，客户常在没登录的设备上点开邮件，所以不要求登录态。
   * 与登录密码重置的三点不同：① 改的是交易密码，**不吊销用户中心会话**；② 不参与登录密码的历史重复校验；
   * ③ 长度上限 **15** 位（MT5 侧口径）。
   *
   * 错误码：42811 链接无效 / 过期；422 不符规则；**40911 MT5 拒绝该密码 —— 链接会被放回，原链接可再试**；
   * **50311 结果未确认 —— 链接不放回**，密码可能已改，页面必须停住、不给重试，让客户先用新密码试登 MT5。
   */
  resetMt5Password: (body: { token: string; newPassword: string }) =>
    api.post<null>('/c/auth/mt5-password/reset', body),
}

/* ==================== 密码规则 ==================== */

/**
 * 密码规则：**8–20 位，须含大写字母、小写字母与数字**，改密时另加「不得与当前密码相同」。
 *
 * 1.0 时接口写的是「≥10 位、四类中任意两类、不与最近 5 次相同」，与需求矛盾；
 * **2.0 已改成与需求一致**（`ChangePasswordRequest` 描述现为「8–20 位，须含大写字母、
 * 小写字母与数字」），所以前端这套判定现在两边对齐了。
 */
export interface PasswordRuleState {
  length: boolean
  upper: boolean
  lower: boolean
  digit: boolean
  /** 仅 MT5 交易密码用到：登录密码不要求符号，那边恒为 true 且不进清单 */
  symbol: boolean
  /** 仅 MT5 交易密码用到：全部字符都是 ASCII 可打印（无中文 / 全角 / 空格）；登录密码恒为 true */
  ascii: boolean
  notSame: boolean
  ok: boolean
}

/**
 * MT5 交易密码规则（接口 7.0）：**8–15 位，且大写 / 小写 / 数字 / 符号四类缺一不可**。
 * 与登录密码差两处——长度上限，以及多出的「符号」一类。
 *
 * 这是 MT5 服务端自己的口径（MetaQuotes：主密码须含四类字符，例 `1Ar#pqkj`；
 * 最小长度由 MT5 组设置决定、下限 8，最大 16）。**规则比后端松一点就会害人**：
 * 2026-09-16 之前这里漏了符号，客户照着勾全打满设了个 `Ikun1111`，我们放行、MT5 拒收，
 * 而当时后端还把它记成成功，客户拿到「密码已重置」却登不上终端。
 * 同一套判定在 cfd-platform 的 `Mt5PasswordRules` 里，两边必须一起改。
 *
 * 同样由 PasswordRuleHint 逐项勾选（rule="mt5"）。
 */
export const MT5_PWD_MAX = 15

/** 符号＝ASCII 可打印区里的非字母非数字（与后端 Mt5PasswordRules.isSymbol 同义）。 */
const MT5_SYMBOL = /[!-\/:-@[-`{-~]/
/** 全部字符都在 ASCII 可打印区 `!`~`~`（与后端 Mt5PasswordRules.isAsciiPrintable 同义）：中文、全角符号、空格都会被 MT5 拒 */
const MT5_ASCII = /^[!-~]+$/

export function checkMt5PasswordRule(pwd: string): PasswordRuleState {
  const length = pwd.length >= 8 && pwd.length <= MT5_PWD_MAX
  const upper = /[A-Z]/.test(pwd)
  const lower = /[a-z]/.test(pwd)
  const digit = /\d/.test(pwd)
  const symbol = MT5_SYMBOL.test(pwd)
  const ascii = MT5_ASCII.test(pwd)
  return {
    length, upper, lower, digit, symbol, ascii, notSame: true,
    ok: length && upper && lower && digit && symbol && ascii,
  }
}

export function checkPasswordRule(pwd: string, current?: string): PasswordRuleState {
  const length = pwd.length >= 8 && pwd.length <= 20
  const upper = /[A-Z]/.test(pwd)
  const lower = /[a-z]/.test(pwd)
  const digit = /\d/.test(pwd)
  // 当前密码为空时（重置流程拿不到旧密码）这一项不参与判定
  const notSame = !current || pwd !== current
  // 登录密码不要求符号与 ASCII（UC §5.2 只列三类）：symbol / ascii 恒 true，PasswordRuleHint 也不为它们画那两条
  return { length, upper, lower, digit, symbol: true, ascii: true, notSame, ok: length && upper && lower && digit && notSame }
}

/* ==================== 开户进度（横幅与拦截弹窗数据源） ==================== */

export interface ProgressView {
  progress: OnboardingProgress
  portalMode: PortalMode
  /** 已完成步数（建档即 2），即「已完成 N/6」的 N */
  completedSteps: number
  /** 当前断点步（3~6） */
  currentStep: number
  currentStepName: string | null
  /** 当前步草稿 JSON —— 开户篇的结构，本篇不解析 */
  draftJson: string | null
  dupFlagged: boolean
  /**
   * 适应性第 1 次未通过的冷却到期时点（UTC ISO；接口 4.0 新增）。
   *
   * `progress=SUIT_FAILED_1` 时非空，其他进度为 null。
   * SUIT1 黄横幅的「解锁日期」与受限弹窗的「6 个月后可重新申请」都取它 ——
   * 4.0 之前这两处的日期位是空的。
   */
  suitabilityCooldownUntil: string | null
}

export const progressApi = {
  /** 能力标签 overview：全模式可读 */
  get: () => api.get<ProgressView>('/c/onboarding/progress'),

  /**
   * 续填跳转令牌。本篇只负责签发并新窗口打开开户页，
   * 兑换（redeem）由开户篇消费 —— 那是另一个项目，本项目不实现。
   *
   * ⚠️ `data` 就是**令牌字符串本身**，不是 `{ token, expiresInSeconds }`
   * （openapi：`data` 为 string「一次性令牌（URL-safe Base64）」）。
   * 这里原先声明成对象、调用方跟着写 `const { token } = …`，从字符串上解构必然得到 undefined，
   * 于是每次「继续开户」都打开 `?resumeToken=undefined`，开户域兑换必然 42811
   * （dev 2026-09-09 实测）。有效期不由响应给，看后端
   * `cfd.customer.onboarding.resume-token-ttl-seconds`。
   */
  resumeToken: () => api.post<string>('/c/onboarding/resume-token'),

  /** 冷却期满重新申请（SUIT1 专用）。冷却未满 42212，进度不符 40911 */
  reapply: () => api.post<null>('/c/onboarding/reapply'),
}
